import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Calendar, Clock, Check, X, AlertTriangle, MessageCircle, User, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  confirmed: { label: "Confirmé", bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/30" },
  completed: { label: "Effectué", bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30" },
  cancelled: { label: "Annulé", bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30" },
  noshow: { label: "No-show", bg: "bg-yellow-500/10", text: "text-yellow-400", border: "border-yellow-500/30" },
};

const WHATSAPP_BASE = "https://wa.me/33";

export function AppointmentsTab() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, upcoming, past, today

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/appointments`);
      setAppointments(res.data);
    } catch { toast.error("Erreur chargement RDV"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAppointments(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/appointments/${id}/status`, { status });
      toast.success(`RDV marqué: ${STATUS_MAP[status]?.label}`);
      fetchAppointments();
    } catch { toast.error("Erreur mise à jour"); }
  };

  const formatDate = (dateStr) => {
    const dt = new Date(dateStr + "T00:00:00");
    const jours = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
    const mois = ["jan", "fév", "mar", "avr", "mai", "jun", "jul", "aoû", "sep", "oct", "nov", "déc"];
    return `${jours[dt.getDay()]} ${dt.getDate()} ${mois[dt.getMonth()]} ${dt.getFullYear()}`;
  };

  const now = new Date();
  const todayStr = now.toISOString().split("T")[0];

  const filtered = appointments.filter(a => {
    if (filter === "today") return a.date === todayStr;
    if (filter === "upcoming") return a.date >= todayStr && a.status === "confirmed";
    if (filter === "past") return a.date < todayStr || a.status !== "confirmed";
    return true;
  });

  const todayCount = appointments.filter(a => a.date === todayStr).length;
  const upcomingCount = appointments.filter(a => a.date >= todayStr && a.status === "confirmed").length;
  const completedCount = appointments.filter(a => a.status === "completed").length;
  const noshowCount = appointments.filter(a => a.status === "noshow").length;

  return (
    <div data-testid="appointments-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Aujourd'hui", value: todayCount, color: "text-[#00FF66]" },
          { label: "À venir", value: upcomingCount, color: "text-blue-400" },
          { label: "Effectués", value: completedCount, color: "text-zinc-400" },
          { label: "No-show", value: noshowCount, color: "text-yellow-400" },
        ].map((stat) => (
          <div key={stat.label} className="bg-[#121212] border border-[#262626] rounded-xl p-4" data-testid={`stat-${stat.label.toLowerCase()}`}>
            <p className="text-xs text-[#666] uppercase tracking-wider">{stat.label}</p>
            <p className={`text-2xl font-bold mt-1 ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-4 h-4 text-[#666]" />
        {[
          { id: "all", label: `Tous (${appointments.length})` },
          { id: "today", label: `Aujourd'hui (${todayCount})` },
          { id: "upcoming", label: `À venir (${upcomingCount})` },
          { id: "past", label: "Passés" },
        ].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filter === f.id ? "bg-[#00FF66] text-black" : "bg-[#1a1a1a] text-[#A3A3A3] hover:text-white"
            }`}
            data-testid={`filter-${f.id}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-[#A3A3A3]">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-[#A3A3A3]">Aucun rendez-vous</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((appt) => {
            const st = STATUS_MAP[appt.status] || STATUS_MAP.confirmed;
            const isPast = appt.date < todayStr;
            const phoneClean = appt.phone?.replace(/^0/, "");

            return (
              <div key={appt.id} className={`bg-[#121212] border border-[#262626] rounded-xl p-4 ${isPast && appt.status === "confirmed" ? "opacity-60" : ""}`}
                data-testid={`appointment-${appt.id}`}>
                <div className="flex items-start justify-between gap-4">
                  {/* Left: Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 bg-[#1a1a1a] rounded-full flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-[#A3A3A3]" />
                      </div>
                      <div>
                        <p className="font-bold text-white text-sm">{appt.name}</p>
                        <p className="text-xs text-[#666]">#{appt.id}</p>
                      </div>
                      <span className={`ml-auto px-2.5 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text} border ${st.border}`}>
                        {st.label}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-[#A3A3A3]">
                      <span className="flex items-center gap-1.5 text-[#00FF66] font-semibold">
                        <Calendar className="w-3.5 h-3.5" /> {formatDate(appt.date)}
                      </span>
                      <span className="flex items-center gap-1.5 text-[#00FF66] font-semibold">
                        <Clock className="w-3.5 h-3.5" /> {appt.time}
                      </span>
                      <a href={`tel:${appt.phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                        <Phone className="w-3.5 h-3.5" /> {appt.phone}
                      </a>
                      {appt.email && (
                        <a href={`mailto:${appt.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                          <Mail className="w-3.5 h-3.5" /> {appt.email}
                        </a>
                      )}
                    </div>

                    {appt.notes && <p className="text-xs text-[#555] mt-2 italic">"{appt.notes}"</p>}
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <a href={`tel:${appt.phone}`} title="Appeler"
                      className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg hover:bg-[#00FF66]/10 hover:text-[#00FF66] text-[#666] transition-all"
                      data-testid={`call-${appt.id}`}>
                      <Phone className="w-3.5 h-3.5" />
                    </a>
                    <a href={`${WHATSAPP_BASE}${phoneClean}`} target="_blank" rel="noopener noreferrer" title="WhatsApp"
                      className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg hover:bg-[#25D366]/10 hover:text-[#25D366] text-[#666] transition-all"
                      data-testid={`whatsapp-${appt.id}`}>
                      <MessageCircle className="w-3.5 h-3.5" />
                    </a>
                    {appt.status === "confirmed" && (
                      <>
                        <button onClick={() => updateStatus(appt.id, "completed")} title="Marquer effectué"
                          className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg hover:bg-blue-500/10 hover:text-blue-400 text-[#666] transition-all"
                          data-testid={`complete-${appt.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => updateStatus(appt.id, "noshow")} title="No-show"
                          className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg hover:bg-yellow-500/10 hover:text-yellow-400 text-[#666] transition-all"
                          data-testid={`noshow-${appt.id}`}>
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => updateStatus(appt.id, "cancelled")} title="Annuler"
                          className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg hover:bg-red-500/10 hover:text-red-400 text-[#666] transition-all"
                          data-testid={`cancel-${appt.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
